import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Building2, Users, DoorOpen } from 'lucide-react';
import axios from 'axios';

const ROOM_TYPES = [
  { value: 'classroom', label: 'Classroom', icon: '🏫' },
  { value: 'lab', label: 'Laboratory', icon: '🔬' },
  { value: 'computer_lab', label: 'Computer Lab', icon: '💻' },
  { value: 'library', label: 'Library', icon: '📚' },
  { value: 'auditorium', label: 'Auditorium', icon: '🎭' },
  { value: 'seminar_hall', label: 'Seminar Hall', icon: '🎤' },
  { value: 'office', label: 'Office', icon: '🏢' },
  { value: 'other', label: 'Other', icon: '📍' }
];

const Classrooms = () => {
  const [classrooms, setClassrooms] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingClassroom, setEditingClassroom] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    room_number: '',
    building: '',
    floor: '',
    room_type: 'classroom',
    capacity: 40,
    has_projector: false,
    has_ac: false,
    description: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchClassrooms();
  }, []);

  const fetchClassrooms = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/classrooms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClassrooms(response.data);
    } catch (error) {
      console.error('Error fetching classrooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const submitData = {
        room_number: formData.room_number,
        building: formData.building,
        floor: formData.floor,
        room_type: formData.room_type,
        capacity: parseInt(formData.capacity),
        has_projector: formData.has_projector,
        has_ac: formData.has_ac,
        description: formData.description
      };

      if (editingClassroom) {
        await axios.put(`${API_URL}/api/master-settings/classrooms/${editingClassroom.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_URL}/api/master-settings/classrooms`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      fetchClassrooms();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving classroom:', error);
      alert('Failed to save classroom. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this classroom?')) {
      setLoading(true);
      try {
        await axios.delete(`${API_URL}/api/master-settings/classrooms/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchClassrooms();
      } catch (error) {
        console.error('Error deleting classroom:', error);
        alert('Failed to delete classroom. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (classroom) => {
    setEditingClassroom(classroom);
    setFormData({
      room_number: classroom.room_number,
      building: classroom.building,
      floor: classroom.floor || '',
      room_type: classroom.room_type || 'classroom',
      capacity: classroom.capacity,
      has_projector: classroom.has_projector || false,
      has_ac: classroom.has_ac || false,
      description: classroom.description || ''
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingClassroom(null);
    setFormData({
      room_number: '',
      building: '',
      floor: '',
      room_type: 'classroom',
      capacity: 40,
      has_projector: false,
      has_ac: false,
      description: ''
    });
  };

  // Get unique buildings for filter
  const buildings = [...new Set(classrooms.map(c => c.building).filter(Boolean))];

  // Filter classrooms
  const filteredClassrooms = classrooms.filter(classroom => {
    const matchesSearch = classroom.room_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (classroom.building && classroom.building.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesBuilding = selectedBuilding === '' || classroom.building === selectedBuilding;
    const matchesType = selectedType === '' || classroom.room_type === selectedType;
    return matchesSearch && matchesBuilding && matchesType;
  });

  // Group by building
  const groupedByBuilding = filteredClassrooms.reduce((acc, classroom) => {
    const building = classroom.building || 'Unknown Building';
    if (!acc[building]) acc[building] = [];
    acc[building].push(classroom);
    return acc;
  }, {});

  const getRoomTypeIcon = (type) => {
    const found = ROOM_TYPES.find(t => t.value === type);
    return found ? found.icon : '📍';
  };

  const getRoomTypeLabel = (type) => {
    const found = ROOM_TYPES.find(t => t.value === type);
    return found ? found.label : 'Other';
  };

  // Stats
  const totalCapacity = classrooms.reduce((sum, c) => sum + (c.capacity || 0), 0);
  const roomsByType = ROOM_TYPES.map(type => ({
    ...type,
    count: classrooms.filter(c => c.room_type === type.value).length
  })).filter(t => t.count > 0);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Classrooms & Rooms</h1>
          <p className="text-sm text-gray-500 mt-1">Manage classrooms, labs, and other rooms</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Room
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
          <p className="text-blue-600 text-sm">Total Rooms</p>
          <p className="text-2xl font-bold text-blue-700">{classrooms.length}</p>
        </div>
        <div className="bg-green-50 p-4 rounded-xl border border-green-200">
          <p className="text-green-600 text-sm">Total Capacity</p>
          <p className="text-2xl font-bold text-green-700">{totalCapacity}</p>
        </div>
        <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
          <p className="text-purple-600 text-sm">Buildings</p>
          <p className="text-2xl font-bold text-purple-700">{buildings.length}</p>
        </div>
        {roomsByType.slice(0, 2).map(type => (
          <div key={type.value} className="bg-gray-50 p-4 rounded-xl border border-gray-200">
            <p className="text-gray-600 text-sm">{type.icon} {type.label}</p>
            <p className="text-2xl font-bold text-gray-700">{type.count}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search rooms..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <select
              value={selectedBuilding}
              onChange={(e) => setSelectedBuilding(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg"
            >
              <option value="">All Buildings</option>
              {buildings.map(building => (
                <option key={building} value={building}>{building}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg"
            >
              <option value="">All Types</option>
              {ROOM_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.icon} {type.label}</option>
              ))}
            </select>
          </div>
          <div className="text-right">
            <span className="text-sm text-gray-600">{filteredClassrooms.length} rooms found</span>
          </div>
        </div>
      </div>

      {/* Rooms List by Building */}
      <div className="space-y-4">
        {Object.keys(groupedByBuilding).length === 0 ? (
          <div className="bg-white rounded-lg p-12 text-center border">
            <DoorOpen className="mx-auto h-12 w-12 text-gray-300" />
            <p className="mt-2 text-gray-500">No rooms found</p>
          </div>
        ) : (
          Object.entries(groupedByBuilding).map(([building, rooms]) => (
            <div key={building} className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b flex items-center gap-3">
                <Building2 className="h-5 w-5 text-blue-600" />
                <div>
                  <h3 className="font-semibold text-gray-800">{building}</h3>
                  <p className="text-xs text-gray-500">{rooms.length} rooms • {rooms.reduce((s, r) => s + (r.capacity || 0), 0)} capacity</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                {rooms.map(classroom => (
                  <div key={classroom.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{getRoomTypeIcon(classroom.room_type)}</span>
                        <div>
                          <p className="font-bold text-gray-800">{classroom.room_number}</p>
                          <p className="text-xs text-gray-500">{getRoomTypeLabel(classroom.room_type)}</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => handleEdit(classroom)} className="p-1 text-blue-600 hover:bg-blue-50 rounded">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleDelete(classroom.id)} className="p-1 text-red-600 hover:bg-red-50 rounded">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-3 text-sm">
                      <span className="flex items-center gap-1 text-gray-600">
                        <Users className="h-4 w-4" /> {classroom.capacity}
                      </span>
                      {classroom.floor && (
                        <span className="text-gray-500">Floor {classroom.floor}</span>
                      )}
                      {classroom.has_projector && <span title="Projector">📽️</span>}
                      {classroom.has_ac && <span title="AC">❄️</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">{editingClassroom ? 'Edit Room' : 'Add Room'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Room Number *</label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border rounded-lg"
                    value={formData.room_number}
                    onChange={(e) => setFormData({ ...formData, room_number: e.target.value })}
                    placeholder="e.g., 101"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Floor</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border rounded-lg"
                    value={formData.floor}
                    onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
                    placeholder="e.g., Ground, 1, 2"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Building *</label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border rounded-lg"
                    value={formData.building}
                    onChange={(e) => setFormData({ ...formData, building: e.target.value })}
                    placeholder="e.g., Main Building"
                    list="buildings"
                  />
                  <datalist id="buildings">
                    {buildings.map(b => <option key={b} value={b} />)}
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Capacity *</label>
                  <input
                    type="number"
                    required
                    min="1"
                    className="w-full px-4 py-2 border rounded-lg"
                    value={formData.capacity}
                    onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Room Type *</label>
                <div className="grid grid-cols-4 gap-2">
                  {ROOM_TYPES.map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, room_type: type.value })}
                      className={`p-2 border-2 rounded-lg text-center transition-all ${
                        formData.room_type === type.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <span className="text-xl">{type.icon}</span>
                      <p className="text-xs mt-1">{type.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.has_projector}
                    onChange={(e) => setFormData({ ...formData, has_projector: e.target.checked })}
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <span className="text-sm">📽️ Has Projector</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.has_ac}
                    onChange={(e) => setFormData({ ...formData, has_ac: e.target.checked })}
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <span className="text-sm">❄️ Has AC</span>
                </label>
              </div>

              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={handleCloseModal} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400">
                  {loading ? 'Saving...' : (editingClassroom ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Classrooms;
